import {
  FileTextOutlined,
  AppstoreOutlined,
  BookOutlined,
  CreditCardOutlined,
  DeploymentUnitOutlined,
  ExperimentOutlined,
  KeyOutlined,
  LineChartOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { playgroundEnabled } from '../lib/gatewayUrl';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { formatTHB } from '../lib/money';
import { useSession } from '../lib/SessionContext';
import { websiteUrl } from '../lib/websiteUrl';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NavUserMenu } from './NavUserMenu';
import { NotificationBell } from './NotificationBell';
import { OrgChooserModal } from './OrgChooserModal';
import { OrgSwitcher } from './OrgSwitcher';
import { PromoCreditWelcomeModal } from './PromoCreditWelcomeModal';
import { ThemeToggle } from './ThemeToggle';

const EASE = [0.165, 0.84, 0.44, 1] as const;
type NavItem = { to: string; label: string; icon: ReactNode };

/**
 * The signed-in app shell: topbar, side navigation, and the page content
 * beneath — one instance for the whole session, with child routes rendered
 * through `<Outlet/>` so the nav never remounts on navigation.
 */
export function Layout({ onSignOut }: { onSignOut: () => void }) {
   const { me, balance, avatarUrl, canManageBilling } = useSession();
   const { dict } = useLanguage();
   const location = useLocation();
   const navigate = useNavigate();
   const homeUrl = websiteUrl();
   const shellRef = useRef<HTMLDivElement>(null);
   const chromeRef = useRef<HTMLDivElement>(null);
   const navRef = useRef<HTMLElement>(null);

   // Pages that require billing/admin access (owner/admin only)
   const adminOnlyPages = ['/topup'];

   // Redirect to dashboard if user loses admin/billing access while on a restricted page
   useEffect(() => {
     if (!canManageBilling && adminOnlyPages.includes(location.pathname)) {
       navigate('/dashboard', { replace: true });
     }
   }, [canManageBilling, location.pathname, navigate]);

  // Fixed mobile chrome needs a measured height so content clears the header
  // without sticky compositing jitter on iOS Safari.
  useLayoutEffect(() => {
    const shell = shellRef.current;
    const chrome = chromeRef.current;
    if (!shell || !chrome) return;

    const syncChromeHeight = () => {
      if (!window.matchMedia('(max-width: 760px)').matches) {
        shell.style.removeProperty('--shell-chrome-h');
        return;
      }
      shell.style.setProperty('--shell-chrome-h', `${chrome.offsetHeight}px`);
    };

    syncChromeHeight();
    const observer = new ResizeObserver(syncChromeHeight);
    observer.observe(chrome);
    window.addEventListener('resize', syncChromeHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncChromeHeight);
    };
  }, [balance, canManageBilling, location.pathname]);

  // On the mobile horizontal strip, the active tab can sit off-screen
  // (Billing / Team / Playground). Scroll the strip horizontally only —
  // scrollIntoView can nudge the page vertically and make sticky chrome jitter.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector<HTMLElement>('.nav-link.active');
    if (!active) return;
    if (!window.matchMedia('(max-width: 760px)').matches) return;

    const navRect = nav.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const targetLeft =
      nav.scrollLeft + (activeRect.left - navRect.left) - (navRect.width - activeRect.width) / 2;
    nav.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
  }, [location.pathname]);

  const brandMark = (
    <>
      <img className="brand-mark" src="/logo-ic.png" alt="" width={36} height={32} aria-hidden="true" />
      <span>IC Lueur Ai</span>
    </>
  );

  const navItems: NavItem[] = [
    { to: '/dashboard', label: dict.common.dashboard, icon: <AppstoreOutlined aria-hidden="true" /> },
    { to: '/keys', label: dict.common.apiKeys, icon: <KeyOutlined aria-hidden="true" /> },
    { to: '/usage', label: dict.common.usage, icon: <LineChartOutlined aria-hidden="true" /> },
    { to: '/reports', label: dict.common.reports, icon: <FileTextOutlined aria-hidden="true" /> },
    { to: '/models', label: dict.common.models, icon: <DeploymentUnitOutlined aria-hidden="true" /> },
    // Billing is admin/owner only (LLMR-55) — a plain member can never top
    // up, so the menu item is not shown to them. Direct navigation to
    // /topup still renders (see TopUp.tsx's restricted panel) rather than
    // redirecting — same "graceful, not broken" treatment as onboarding.
    ...(canManageBilling ? [{ to: '/topup', label: dict.common.topUp, icon: <CreditCardOutlined aria-hidden="true" /> }] : []),
    { to: '/team', label: dict.common.team, icon: <TeamOutlined aria-hidden="true" /> },
    // Dev/local only — needs PUBLIC_PLAYGROUND plus PUBLIC_GATEWAY_URL so
    // baking a gateway origin for Quick Start snippets does not also ship
    // this tester on uat/prod. See src/lib/gatewayUrl.ts.
    ...(playgroundEnabled() ? [{ to: '/playground', label: dict.common.playground, icon: <ExperimentOutlined aria-hidden="true" /> }] : []),
  ];

  return (
    <div className="shell brand-page" ref={shellRef}>
      <OrgChooserModal />
      <PromoCreditWelcomeModal />
      <a className="skip-link" href="#main">
        {dict.common.skipToContent}
      </a>

      <div className="shell-chrome" ref={chromeRef}>
        <header className="shell-topbar">
          {homeUrl ? (
            <a className="brand" href={homeUrl} aria-label={dict.common.backToWebsite}>
              {brandMark}
            </a>
          ) : (
            <div className="brand">{brandMark}</div>
          )}
          <div className="shell-topbar-actions">
            {/* The balance is read from SessionContext, which reads it from the
                server — never derived locally. A chip here is a convenience
                link to /topup, not a second source of truth. */}
            {/* Overview and Billing already lead with the balance — hide the chip
                there so the same number isn't emphasized twice in one viewport. */}
            {balance !== null && location.pathname !== '/dashboard' && location.pathname !== '/topup' && (
              <NavLink to="/topup" className="balance-chip">
                {formatTHB(balance)}
              </NavLink>
            )}
            <OrgSwitcher className="org-switcher--topbar" />
            <LanguageSwitcher />
            <ThemeToggle />
            <NotificationBell />
            <NavUserMenu
              email={me?.email ?? ''}
              displayName={me?.display_name ?? undefined}
              username={me?.username ?? undefined}
              avatarUrl={avatarUrl}
              mobilePanelExtra={<OrgSwitcher variant="inline" className="org-switcher--mobile-menu" />}
              personalLabel={dict.common.personal}
              editProfileLabel={dict.common.editProfile}
              signOutLabel={dict.common.signOut}
              onSignOut={onSignOut}
            />
          </div>
        </header>

        <nav ref={navRef} className="shell-nav" aria-label={dict.common.primaryNav}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
          <div className="nav-divider" aria-hidden="true" />
          <NavLink to="/guide" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <BookOutlined aria-hidden="true" />
            {dict.common.guide}
          </NavLink>
        </nav>
      </div>

      <main id="main" className="shell-content">
          {/* Keyed by pathname so a route change is a genuine mount/unmount
              pair for AnimatePresence to animate between, not a prop update
              on the same element. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: EASE }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
    </div>
  );
}
